import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox, MeshDistortMaterial } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, OBSTACLE_HEIGHT, FLOOR_HEIGHT, TILE_COLORS } from '../constants';
import { LavaParticles } from '../shared/Particles3D';
import type { GraphicsQuality } from '../GameBoard3D';

interface LavaTile3DProps {
  position: [number, number, number];
  entranceDelay?: number;
  graphics?: GraphicsQuality;
}

export default function LavaTile3D({ position, entranceDelay = 0, graphics = 'high' }: LavaTile3DProps) {
  const groupRef = useRef<THREE.Group>(null);
  const elapsed = useRef(0);
  const entered = useRef(false);

  const tileY = FLOOR_HEIGHT + OBSTACLE_HEIGHT / 2;

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    elapsed.current += delta;

    if (!entered.current) {
      const t = Math.max(0, elapsed.current - entranceDelay);
      const progress = Math.min(t * 4, 1);
      groupRef.current.scale.set(progress, progress, progress);
      if (progress >= 1) entered.current = true;
    }
  });

  return (
    <group ref={groupRef} position={[position[0], 0, position[2]]} scale={0}>
      {/* Lava surface with distort material for molten effect */}
      <RoundedBox
        args={[TILE_SIZE * 0.88, OBSTACLE_HEIGHT, TILE_SIZE * 0.88]}
        radius={0.03}
        smoothness={2}
        position={[0, tileY, 0]}
      >
        <MeshDistortMaterial
          color={TILE_COLORS.lava.top}
          emissive={TILE_COLORS.lava.glow}
          emissiveIntensity={1}
          distort={0.3}
          speed={3}
          roughness={0.2}
        />
      </RoundedBox>

      {/* Rising lava particles (scaled by quality) */}
      <group position={[0, tileY, 0]}>
        <LavaParticles
          count={graphics === 'high' ? 30 : graphics === 'medium' ? 15 : 8}
          bounds={[TILE_SIZE * 0.7, OBSTACLE_HEIGHT, TILE_SIZE * 0.7] as [number, number, number]}
        />
      </group>

      {/* Warm glow light */}
      <pointLight
        position={[0, tileY + 0.3, 0]}
        color={TILE_COLORS.lava.glow}
        intensity={2}
        distance={2.5}
        decay={2}
      />
    </group>
  );
}
